<template>
  <div>
    <!-- Rendered inside VerificationsWorkspace, which owns the title and tabs. -->
    <div class="page-section pt-0">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search by participant or license number"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
              <div class="col-lg d-flex flex-wrap justify-content-end">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px"
                  label="text"
                  v-model="statusFilter"
                  :reduce="(item) => item.value"
                  placeholder="All statuses"
                  :options="statusFilterOptions"
                  @input="fetchLicenses()"
                />
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="licenses"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            show-empty
            empty-text="No licenses submitted yet."
            @sort-changed="onSortChange"
          >
            <template #cell(student)="data">
              <strong>{{ get(data.item, 'student.user.first_name') }} {{ get(data.item, 'student.user.last_name') }}</strong>
            </template>

            <template #cell(credential_type)="data">
              {{ credentialTypeLabel(data.item.credential_type) }}
            </template>

            <template #cell(state)="data">
              {{ data.item.state || '—' }}
            </template>

            <template #cell(status)="data">
              <b-badge
                :class="`border border-${statusMeta(data.item.status).variant} text-${
                  statusMeta(data.item.status).variant
                }`"
                variant="none"
                >{{ statusMeta(data.item.status).label }}</b-badge
              >
            </template>

            <template #cell(documents)="data">
              <b-badge variant="none" class="border border-secondary text-secondary">
                {{ (data.item.documents || []).length }}
              </b-badge>
            </template>

            <template #cell(verified_at)="data">
              <template v-if="data.item.verified_at">
                {{ formatDate(data.item.verified_at) }}
                <small class="text-muted d-block">
                  {{ data.item.verification_source === 'skillform' ? 'Skillform' : verifierName(data.item) }}
                </small>
              </template>
              <span v-else>—</span>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <b-btn variant="light" size="sm" @click="openReview(data.item)">Review</b-btn>
            </template>
          </b-table>

          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalLicenses"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="license-verifications-table"
            />
          </div>
        </div>
      </div>
    </div>

    <b-modal
      v-model="showReview"
      :title="reviewTitle"
      centered
      size="md"
      ok-title="Save"
      :ok-disabled="isSaving"
      @ok.prevent="saveReview"
    >
      <template v-if="reviewItem">
        <p class="mb-3">
          {{ credentialTypeLabel(reviewItem.credential_type) }} · {{ reviewItem.state || 'No state' }} ·
          <strong>{{ reviewItem.license_number }}</strong>
        </p>

        <b-form-group label="Status" label-class="form-label">
          <b-form-radio-group v-model="reviewStatus" :options="reviewStatusOptions" stacked />
        </b-form-group>

        <b-form-group label="Note" label-class="form-label">
          <b-form-textarea
            v-model="reviewNote"
            rows="3"
            placeholder="How you checked it, e.g. Georgia registry, active through 2027"
          />
        </b-form-group>

        <page-separator title="Proof" />
        <div v-for="doc in reviewItem.documents || []" :key="doc.id" class="mb-1">
          <a :href="doc.file_url" target="_blank" rel="noopener">{{ doc.filename || 'View file' }}</a>
          <small class="text-muted ml-1">{{ documentKindLabel(doc.kind) }}</small>
        </div>
        <p v-if="!(reviewItem.documents || []).length" class="text-muted mb-2">No proof uploaded yet.</p>
        <b-form-group label="Add registry screenshot" label-for="registry_file" label-class="form-label">
          <b-form-file
            id="registry_file"
            placeholder="Select a file"
            accept="image/*,application/pdf"
            :browse-text="$t('generalMsgs.browse')"
            v-model="selectedUploadFile"
            @input="uploadRegistryScreenshot"
            :disabled="isDocUploading"
          />
          <small v-if="isDocUploading"><i class="fas fa-circle-notch fa-spin"></i> Uploading…</small>
        </b-form-group>

        <template v-if="(reviewItem.events || []).length">
          <page-separator title="History" />
          <div v-for="event in reviewItem.events" :key="event.id" class="mb-2">
            <small class="text-muted">
              {{ formatDate(event.created_at) }} · {{ statusMeta(event.status).label }}
              <template v-if="event.actor"> · {{ event.actor.first_name }} {{ event.actor.last_name }}</template>
            </small>
            <div v-if="event.note">{{ event.note }}</div>
          </div>
        </template>
      </template>
    </b-modal>
  </div>
</template>

<script>
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';
import moment from 'moment';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import {
  LICENSE_STATUS_META,
  credentialTypeLabel,
  documentKindLabel,
} from '../../../common/licenseVerification';

export default {
  components: {
    MdIcon,
    PageSeparator,
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'License Verifications',
      searchTerm: '',
      statusFilter: null,
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalLicenses: 0,
      ordering: null,
      licenses: [],

      showReview: false,
      reviewItem: null,
      reviewStatus: 'verified',
      reviewNote: '',
      isSaving: false,
      selectedUploadFile: null,
      isDocUploading: false,
      reviewStatusOptions: [
        { value: 'verified', text: 'Verified' },
        { value: 'needs_review', text: 'Needs Review' },
        { value: 'not_verified', text: 'Not Verified' },
      ],
    };
  },

  computed: {
    statusFilterOptions() {
      return Object.keys(LICENSE_STATUS_META).map((value) => ({ value, text: LICENSE_STATUS_META[value].label }));
    },

    reviewTitle() {
      if (!this.reviewItem) return 'Review License';
      return `${get(this.reviewItem, 'student.user.first_name', '')} ${get(this.reviewItem, 'student.user.last_name', '')}`;
    },

    tableColumns() {
      return [
        { key: 'student', label: 'Participant' },
        { key: 'credential_type', label: 'License', sortable: true },
        { key: 'state', label: 'State' },
        { key: 'license_number', label: 'License Number' },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'documents', label: 'Proof' },
        { key: 'verified_at', label: 'Verified', sortable: true },
        { key: 'actions', label: '' },
      ];
    },
  },

  methods: {
    ...mapActions('licenseVerification', [
      'getAllLicenseVerifications',
      'reviewLicenseVerification',
      'getLicenseVerification',
      'createLicenseDocument',
      'getLicenseDocUploadPresignedUrl',
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    get,
    credentialTypeLabel,
    documentKindLabel,

    // Saved straight away: the license already exists, unlike the student form.
    async uploadRegistryScreenshot() {
      if (!this.selectedUploadFile || !this.reviewItem) return;
      this.isDocUploading = true;
      try {
        const urlResp = await this.getLicenseDocUploadPresignedUrl({
          file_name: this.selectedUploadFile.name,
          content_type: this.selectedUploadFile.type,
          upload_type: 'registry_screenshot',
        });
        await this.uploadToPresignedUrl({ url: urlResp.upload_url, file: this.selectedUploadFile });
        await this.createLicenseDocument({
          license_verification: this.reviewItem.id,
          kind: 'registry_screenshot',
          file_url: urlResp.upload_url.split('?')[0],
          filename: this.selectedUploadFile.name,
        });
        this.reviewItem = (await this.getLicenseVerification(this.reviewItem.id)).data;
        this.makeToast({ variant: 'success', msg: 'Screenshot added.' });
        this.fetchLicenses(this.currentPage);
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Upload failed. Please try again.' });
      }
      this.selectedUploadFile = null;
      this.isDocUploading = false;
    },

    statusMeta(status) {
      return LICENSE_STATUS_META[status] || LICENSE_STATUS_META.pending;
    },

    formatDate(value) {
      return value ? moment(value).format('MM/DD/YYYY') : '';
    },

    verifierName(item) {
      const user = item.verified_by;
      return user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '';
    },

    openReview(item) {
      this.reviewItem = item;
      this.reviewStatus = ['verified', 'needs_review', 'not_verified'].includes(item.status) ? item.status : 'verified';
      this.reviewNote = '';
      this.showReview = true;
    },

    async saveReview() {
      if (!this.reviewNote.trim()) {
        this.makeToast({ variant: 'danger', msg: 'Add a note about how you checked this license.' });
        return;
      }
      this.isSaving = true;
      try {
        await this.reviewLicenseVerification({
          id: this.reviewItem.id,
          data: { status: this.reviewStatus, note: this.reviewNote.trim() },
        });
        this.showReview = false;
        this.makeToast({ variant: 'success', msg: 'License review saved.' });
        this.fetchLicenses(this.currentPage);
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isSaving = false;
    },

    async fetchLicenses(pageNum = 1) {
      this.isLoading = true;
      try {
        const response = await this.getAllLicenseVerifications({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.statusFilter && { status: this.statusFilter }),
        });
        this.licenses = response.data.results;
        this.currentPage = pageNum;
        this.totalLicenses = response.data.count;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchLicenses(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchLicenses();
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce((vm) => {
      vm.fetchLicenses();
    }, 500),
  },

  async mounted() {
    await this.fetchLicenses();
  },
};
</script>
